/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { datadogLogs } from "@datadog/browser-logs"
import {
  datadogRum,
  DefaultPrivacyLevel,
  RumInitConfiguration,
} from "@datadog/browser-rum"
import { isNotNoise } from "@grab-food/web-monitoring-helpers"
import {
  CUSTOM_ERROR,
  DEFAULT_DATADOG_RUM_CONFIG,
  LOG_COMPONENT_STATE,
  LOG_NAME_SEPARATOR,
  SESSION_START,
  getIgnoreErrorMessagesRegex,
} from "constants/datadog"
import { isPrd } from "constants/domain"
import { GrabXRUMConfig } from "grabx"
import { storageHelper } from "utils/storage"
import { readAuthnTokenJti } from "utils/authnCookie"
import { getMerchantGrabID } from "utils/user_helper"
import { getMexAppInfo, getMexAppVersionFacet, isWebview } from "utils/webview"

const SITE = "datadoghq.com"
const SERVICE_NAME = "food-merchant"

// Pre-init `datadogLogs.logger.*` calls are not no-ops: the SDK deep-clones the
// common context and buffers up to 500 closures that are only drained by init().
// When logs are disabled init() never runs, so the helpers gate on this instead.
let logsInitialised = false

/** `window.config.datadog`, i.e. the per-environment credentials from `src/config/*`. */
export interface DatadogClientConfig {
  applicationId: string
  clientToken: string
  env: string
}

export interface DatadogLogContext {
  [key: string]: unknown
  gkTraceId?: string
}

/**
 * User attributes attached to every log, so a log line can be traced back to the
 * merchant/user that produced it (mirrors the RUM/Scribe attribute naming).
 *
 * NOTE: keep in sync with `getDefaultParameters()` in `src/utils/tracking.ts` —
 * Scribe computes the same attributes; if a derivation changes (e.g. MEX_TYPE),
 * change it in both places or the dashboards silently disagree.
 */
export const getLoggerUserContext = (): Record<string, string> => {
  const userProfileInfo = storageHelper.getUserProfile()
  const userProfile = userProfileInfo?.user_profile

  return {
    USER_GRAB_ID: userProfile?.grab_id ?? "",
    MERCHANT_GRAB_ID: getMerchantGrabID() ?? "",
    PARENT_ENTITY_ID: userProfile?.parent_entity_id ?? "",
    USER_ROLE: userProfile?.role ?? "",
    MEX_TYPE: userProfileInfo?.business_line_ids?.join("/") ?? "",
  }
}

/**
 * Host-app attributes attached to every log AND every RUM event from init time.
 * Build facts rather than user identifiers, so unlike `getLoggerUserContext`
 * they need not be deferred to the first attributable log.
 *
 * AUTHN_TOKEN_JTI is the one exception and is deliberate: it identifies the
 * SESSION'S TOKEN, not the merchant. The WebView Auth Migration runbook asks for
 * it by name as the join key to the backend's record of the same token. It
 * cannot authenticate — its HttpOnly twin `mexusers_authn_token`, which can, is
 * unreadable here by design and is never logged — but it IS this app's
 * anti-CSRF token (`utils/csrf` mirrors this same cookie into X-CSRF-Token),
 * which the runbook's "opaque" note does not account for. Shipping it in full
 * is a deliberate call by the MR author on !2823: 14-day Datadog retention, an
 * internal Scribe sink, and a short-lived cookie bound the exposure, and a
 * leaked value is only usable if CORS is also misconfigured. Re-weigh that
 * before widening where this attribute goes. Read ONCE at init like the rest of this context, so it is the jti
 * the page loaded with; a mid-session `refreshAuthSession` rotates the cookie
 * without updating this. Scribe re-reads per event, so a jti on
 * WEBVIEW_SESSION_ERROR is the value at the moment of failure — the two are
 * meant to differ that way, and the Datadog one is the session-start reading.
 *
 * NOTE: the four build keys are also emitted to Scribe by `getDefaultParameters()` in
 * `src/utils/tracking.ts` — same sync obligation as `getLoggerUserContext`. Same
 * names and same derivations, so the two systems segment on the same values;
 * MEX_APP_VERSION in particular is the `dvc` facet form ("mex-ios-4.146.0") both
 * there and here, not the bare `appVersion`.
 *
 * One deliberate difference: this context is read ONCE at init, where Scribe
 * re-reads it per event. That is what makes SESSION_START a valid denominator —
 * every log of a page load carries the value the session started with. It also
 * means the two can disagree for the `?webview=true` desktop override, whose
 * query param a `history` push can drop mid-session (Scribe would flip to false,
 * this stays true). Inside the app the UA carries it, so neither can drift.
 *
 * Values are strings because the logger context is `Record<string, string>`, so
 * IS_WEBVIEW is stringified here where Scribe's facet is a real boolean. Missing
 * fields coerce to "" rather than undefined: the SDK's `combine` drops undefined
 * keys, which would leave the attribute absent instead of falsy in Datadog.
 * Note this is the opposite of Scribe, which DROPS empty-string facets — so an
 * absent jti reads as "" here and is simply missing there.
 */
export const getLoggerAppContext = (): Record<string, string> => {
  const mexAppInfo = getMexAppInfo()
  const inWebview = isWebview()

  return {
    // The only attribute that identifies an app-webview session. MEX_APP_CHANNEL
    // cannot stand in for it: that is "" on production Grab Merchant builds, so
    // webview and plain-browser sessions are indistinguishable without this.
    IS_WEBVIEW: String(inWebview),
    MEX_APP_VERSION: getMexAppVersionFacet() ?? "",
    MEX_APP_CHANNEL: mexAppInfo?.appChannel ?? "",
    OS_VERSION: mexAppInfo?.osVersion ?? "",
    // Cohort split for the WebView cookie-auth rollout (Scribe's USING_COOKIES).
    // It belongs in THIS context, not on the individual error logs: SESSION_START
    // is the denominator of every log-based session rate, so a cookie-auth error
    // rate whose numerator alone carried the attribute would divide by ALL
    // sessions. Stable for the life of the page load by construction — see
    // `isCookieAuthEnabled`, which every HTTP client also resolves once at module
    // load — so reading it here at init cannot drift from what the calls did.
    USING_COOKIES: String(storageHelper.isCookieAuthEnabled()),
    // "" when the app injected nothing, which is a real reading rather than a
    // gap: paired with USING_COOKIES it separates "no cookie was offered" from
    // "a cookie was offered and rejected".
    //
    // Webview-only, and that scoping is load-bearing rather than tidiness. The
    // ORDINARY weblogin portal authenticates with this same cookie — `utils/csrf`
    // reads it on every desktop session — so reading it unconditionally would
    // ship a per-session token id to Datadog and RUM for every merchant on the
    // portal, not just the migration cohort this exists to measure. Scribe is
    // scoped the same way by construction: its jti only rides the WEBVIEW_*
    // events, which no-op outside the app.
    AUTHN_TOKEN_JTI: inWebview ? readAuthnTokenJti() : "",
  }
}

/**
 * Adds keys to the logger's global context without dropping what is already
 * there — the context has two writers (app context at init, user context on the
 * first attributable log) and `setGlobalContext` REPLACES the whole object.
 *
 * Per-key via the SDK's own `setGlobalContextProperty`, which assigns into the
 * live context (`setContextProperty` in browser-core 4.25.0), so not clobbering
 * is a property of the call rather than a rule this helper has to be trusted to
 * follow.
 */
const addToGlobalContext = (context: Record<string, string>): void => {
  Object.entries(context).forEach(([key, value]) => {
    datadogLogs.setGlobalContextProperty(key, value)
  })
}

/**
 * RUM counterpart of `addToGlobalContext`, so the webview RUM errors
 * ("Webview session error", "Webview session error retry", "Webview bottom nav
 * bridge error") are sliceable by the same attributes as the logs.
 *
 * Verified against the pinned SDK rather than the current docs: v4.25 already
 * exposes `setGlobalContextProperty` (`browser-rum-core` `rumPublicApi.js`, on a
 * context manager created at module load, so pre- and post-init calls both
 * stick), and `assembly.js` combines that context into every event's `context`
 * field — i.e. `@context.IS_WEBVIEW` in Datadog.
 */
const addToRumGlobalContext = (context: Record<string, string>): void => {
  Object.entries(context).forEach(([key, value]) => {
    datadogRum.setGlobalContextProperty(key, value)
  })
}

/** Initialises Datadog RUM from the `portalDatadogRumConfig` GrabX flag. */
const setupRum = (
  clientConfig: DatadogClientConfig | undefined,
  config: GrabXRUMConfig,
  ignoreErrorMessagesRegex: RegExp[],
): void => {
  // No credentials (dev-config fallback) — RUM cannot report anyway.
  if (!clientConfig?.clientToken) {
    return
  }

  // Before init, for the same reason the logs context precedes
  // emitSessionStart: `trackViews` calls `triggerViewUpdate()` synchronously
  // during `datadogRum.init`, so the first view document (version 1) is
  // assembled while the global context is still empty. Later versions of that
  // same view supersede it, so this is a small loss — but the context manager
  // exists from module load and init only reads it through a callback, so
  // there is nothing to gain by waiting.
  addToRumGlobalContext(getLoggerAppContext())

  // context: https://grab.slack.com/archives/C02JR6U2E8L/p1747908703563939
  const { sampleRate, sessionReplaySampleRate } = config ?? {}
  const sampleRateConfig =
    // 0 or undefined values will cause errors to not be sent as well (instead of just deactivating RUM) - so we omit the values if they're both missing
    sampleRate && sessionReplaySampleRate
      ? {
          sampleRate,
          sessionReplaySampleRate,
        }
      : {}

  // NOTE: SDK v4.25 calls this option `trackInteractions` - `trackUserInteractions`
  // only exists from v4.31, so the SDK ignores this key today. Kept unchanged to
  // preserve current behaviour; renaming it would start capturing every user
  // interaction as soon as the flag sets it to true.
  const trackUserInteractionsConfig = {
    trackUserInteractions: config.trackUserInteractions,
  } as Partial<RumInitConfiguration>

  datadogRum.init({
    ...clientConfig,
    ...sampleRateConfig,
    ...trackUserInteractionsConfig,

    site: SITE,
    service: SERVICE_NAME,
    version: process.env.APP_VERSION,
    // the flag types this as a plain string, the SDK as its own union
    defaultPrivacyLevel: config.defaultPrivacyLevel as DefaultPrivacyLevel,
    trackLongTasks: config.trackLongTasks,
    trackResources: config.trackResources,
    beforeSend: event => {
      const errorMessage =
        event.type === "error" ? event.error?.message ?? "" : ""
      if (
        errorMessage &&
        ignoreErrorMessagesRegex.some(ignoreErrorMessageRegex =>
          ignoreErrorMessageRegex.test(errorMessage),
        )
      ) {
        return false
      }
      return true
    },
  })
}

// Every browser log this portal sends is an error: `forwardErrorsToLogs` plus
// the `ddLog*Error` helpers, and nothing emits at `info`. Any dashboard dividing
// "sessions with errors" by "all sessions" therefore gets 1 by construction and
// reports a 0% error-free session rate, because both sides count the same
// sessions. This is that missing denominator: one non-error log per page load.
//
// It marks the start of a session rather than recurring through one, so a
// session the SDK renews without a page load goes uncounted. Verified against
// @datadog/browser-core@4.25.0: a session ends after 15 minutes of inactivity
// (SESSION_EXPIRATION_DELAY) or 4 hours absolute (SESSION_TIME_OUT_DELAY), and
// is renewed by user activity — sending a log never extends it. Measured on
// 2026-08-10 (BP-55232) at ~1.44 sessions per user per day (2,203 users / 3,174
// sessions over 24h), with 2% of sessions reaching the 4h cap, so the shortfall
// leaves the rate slightly pessimistic rather than wrong. A session-id poll was
// built and dropped on those numbers; re-check them before reviving it.
const emitSessionStart = (): void => {
  // Undefined while the session is expired or was sampled out of logging. Both
  // states also drop error logs, so there is no denominator to maintain — and
  // skipping keeps this log from being the thing that defines a session.
  if (!datadogLogs.getInternalContext()?.session_id) {
    return
  }
  datadogLogs.logger.info(SESSION_START)
}

/**
 * Initialises Datadog Browser Logs. Gated by `enableLogs` on the
 * `portalDatadogRumConfig` GrabX flag, and shares its `ignoreErrorMessages` rules
 * with RUM so a noisy error is silenced in one place.
 */
const setupLogs = (
  clientConfig: DatadogClientConfig | undefined,
  config: GrabXRUMConfig,
  ignoreErrorMessagesRegex: RegExp[],
): void => {
  // clientToken guard: a production build served from a host that falls back to
  // the dev config has no `datadog` key — init would throw before the app renders.
  if (!config?.enableLogs || !clientConfig?.clientToken) {
    return
  }

  datadogLogs.init({
    clientToken: clientConfig.clientToken,
    site: SITE,
    service: SERVICE_NAME,
    env: clientConfig.env,
    version: process.env.APP_VERSION,
    forwardErrorsToLogs:
      config.forwardErrorsToLogs ??
      DEFAULT_DATADOG_RUM_CONFIG.forwardErrorsToLogs,
    telemetrySampleRate: 0,
    beforeSend: event => {
      // Redaction first: the SDK swallows beforeSend exceptions (sending the log
      // as-is), so the scrub must not depend on anything below that could throw.
      // forwardErrorsToLogs ships raw 5xx response bodies as error.stack — redact
      // so error payloads echoing merchant/user identifiers never reach Datadog.
      // Status-0 failures (offline/CORS/abort) carry a JS stack trace, kept.
      if (
        event.origin === "network" &&
        event.error &&
        (event.http?.status_code ?? 0) >= 500
      ) {
        Object.assign(event.error, { stack: "[redacted response body]" })
      }

      // Downgrade food-cms fetch errors to warn — these are non-critical CMS
      // asset/config fetches and inflate the error count unnecessarily.
      if (
        event.status === "error" &&
        /^Fetch error GET https:\/\/food-cms\.grab\.com\/.*\.(jpe?g|png|gif|webp|svg)(\?|$)/.test(
          event.message,
        )
      ) {
        Object.assign(event, { status: "warn" })
      }

      // The profile is only available after login, so the context is attached on the
      // first log instead of at init time. Gate on USER_GRAB_ID (not emptiness) so
      // unrelated global-context keys can't block attribution, and merge so they
      // are preserved.
      //
      // SESSION_START is excluded deliberately. It is the first log of every page
      // load, so enriching it would widen identifier egress from "sessions that
      // errored" to "every session that opened the portal" — a per-page-load record
      // of which merchant and user were on it. The denominator only needs
      // @session_id, and the first error log still primes the context exactly as it
      // does today, so the identifiers reach Datadog for every session that has
      // something to attribute and no others.
      const globalContext = datadogLogs.getGlobalContext()
      if (event.message !== SESSION_START && !globalContext.USER_GRAB_ID) {
        const userContext = getLoggerUserContext()
        if (userContext.USER_GRAB_ID) {
          // The SDK assembles the event with the global context *before* calling
          // beforeSend, so the current event must be enriched directly — updating
          // the global context only covers subsequent logs.
          Object.assign(event, userContext)
          addToGlobalContext(userContext)
        }
      }

      return isNotNoise(event, ignoreErrorMessagesRegex)
    },
  })

  // Derived from the SDK, not assumed: init() aborts silently on an invalid
  // config (e.g. an out-of-range logSampleRate hand-edited on the GrabX flag),
  // and getInitConfiguration() only returns a value once init really succeeded.
  logsInitialised = !!datadogLogs.getInitConfiguration()

  if (logsInitialised) {
    // Before emitSessionStart: that log is the denominator of every log-based
    // session-rate widget, so an attribute missing from it could filter the
    // numerator only — a webview error rate would then divide webview errors by
    // ALL sessions. Verified against @datadog/browser-logs@4.25.0: `assembly.js`
    // combines the global context into the log before `beforeSend` runs, so
    // SESSION_START itself carries these keys.
    addToGlobalContext(getLoggerAppContext())
    emitSessionStart()
  }
}

/**
 * Single entry point for Datadog monitoring: RUM always, Browser Logs when the
 * `portalDatadogRumConfig` GrabX flag enables them. Both share the same client
 * credentials and the same `ignoreErrorMessages` rules.
 */
export const initDatadog = (
  // `undefined` when a build is served from a host that falls back to the dev
  // config (no `datadog` key) — index.jsx is not type-checked, so keep this honest.
  clientConfig: DatadogClientConfig | undefined,
  config: GrabXRUMConfig,
): void => {
  const ignoreErrorMessagesRegex = getIgnoreErrorMessagesRegex(config)

  setupRum(clientConfig, config, ignoreErrorMessagesRegex)
  setupLogs(clientConfig, config, ignoreErrorMessagesRegex)
}

const logToConsole = (message: string, context: object): void => {
  if (!isPrd()) {
    // eslint-disable-next-line no-console
    console.log("DD Monitoring", message, context)
  }
}

/** Splits `gkTraceId` out of the context so it stays searchable as a top level attribute. */
const buildErrorContext = (
  attributes: object,
  { gkTraceId, ...extraParams }: DatadogLogContext,
): object => {
  return {
    ...attributes,
    ...(gkTraceId ? { gk_trace_id: gkTraceId } : {}),
    errorInfo: extraParams,
  }
}

/**
 * Logs that a component was rendered, e.g.
 * `ddLogComponentView("order-list", "StoreSelector")`.
 */
export const ddLogComponentView = (
  contextName: string,
  componentName: string | string[],
): void => {
  const nameArray = Array.isArray(componentName)
    ? componentName
    : [componentName]
  const concatenatedNames = [contextName, ...nameArray].join(LOG_NAME_SEPARATOR)
  const message = `Component: ${concatenatedNames} | ${LOG_COMPONENT_STATE.view}`
  const logContext = { "component-name": concatenatedNames }

  logToConsole(message, logContext)
  if (!logsInitialised) {
    return
  }
  datadogLogs.logger.info(message, logContext)
}

/**
 * Logs a non-API error, e.g.
 * `ddLogCustomError("local-storage", "quota exceeded", { key: "menu" })`.
 * `message` is the actual error text and is carried in `errorInfo.message`;
 * the log message is the constant `CUSTOM_ERROR` so DD dashboards can group on it.
 */
export const ddLogCustomError = (
  type: string,
  message: string,
  extraParams: DatadogLogContext = {},
): void => {
  // `message` last: the dedicated argument wins over a `message` key in extraParams
  const logContext = buildErrorContext({ error_type: type }, {
    ...extraParams,
    message,
  } as DatadogLogContext)

  logToConsole(CUSTOM_ERROR, logContext)
  if (!logsInitialised) {
    return
  }
  datadogLogs.logger.error(CUSTOM_ERROR, logContext)
}
